/**
 * Per-category product detail layouts.
 * Each layout is a focused, lightweight section block that only renders
 * fields relevant to that category. PerfumePage keeps the rich existing
 * detail layout in ProductPage.js untouched (this dispatcher only intercepts
 * non-perfume categories).
 */
import { Sparkles, Bike, Car, Home, Droplet, ShieldCheck, Clock, Wind } from 'lucide-react';

/* ─── Helmet Spray ─── */
export function HelmetSprayBlocks({ product }) {
  return (
    <div className="space-y-4 my-6" data-testid="layout-helmet-spray">
      <Card icon={Bike} title="Use cases">
        <ul className="space-y-1.5 text-sm text-white/70 font-sans">
          <li className="flex gap-2"><span className="text-[#1C7D3F]">◆</span> Daily motorcycle commute (rider helmet, sweat odor)</li>
          <li className="flex gap-2"><span className="text-[#1C7D3F]">◆</span> Shared / rented helmets — sanitize before use</li>
          <li className="flex gap-2"><span className="text-[#1C7D3F]">◆</span> Foot odor inside boots, gym gear, gloves</li>
          <li className="flex gap-2"><span className="text-[#1C7D3F]">◆</span> Smell-clean any fabric in seconds</li>
        </ul>
      </Card>
      <Card icon={Wind} title="How to use">
        <ol className="space-y-1.5 text-sm text-white/70 font-sans list-decimal list-outside ml-5">
          <li>Hold bottle 15-20cm from interior fabric</li>
          <li>Spray 3-5 pumps evenly inside helmet padding</li>
          <li>Let air-dry for 60 seconds before wearing</li>
          <li>Repeat after every long ride for best results</li>
        </ol>
      </Card>
    </div>
  );
}

/* ─── Car Spray ─── */
export function CarSprayBlocks() {
  return (
    <div className="space-y-4 my-6" data-testid="layout-car-spray">
      <Card icon={Car} title="Best for">
        <div className="grid grid-cols-2 gap-2 text-sm text-white/70 font-sans">
          <span className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Sedans</span>
          <span className="flex gap-2"><span className="text-[#D4AF37]">◆</span> SUVs</span>
          <span className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Pickup trucks</span>
          <span className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Motorcycles</span>
          <span className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Rideshare drivers</span>
          <span className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Trucks</span>
        </div>
      </Card>
      <Card icon={Clock} title="Longevity">
        <p className="text-sm text-white/70 font-sans">
          One bottle lasts 2-4 weeks of daily driving. Spray onto upholstery, dashboard, or floor mat once a day for steady scent.
          For a stronger hit, mist directly into the AC vent before turning it on.
        </p>
      </Card>
    </div>
  );
}

/* ─── Home & Linen ─── */
export function HomeLinenBlocks() {
  return (
    <div className="space-y-4 my-6" data-testid="layout-home-linen">
      <Card icon={Home} title="Room size guide">
        <div className="grid grid-cols-2 gap-2 text-sm text-white/70 font-sans">
          <div><strong className="text-white">100ml</strong> — bedrooms, walk-ins, bathrooms</div>
          <div><strong className="text-white">250ml</strong> — living rooms, offices</div>
          <div><strong className="text-white">500ml</strong> — open-plan, lobbies, retail</div>
        </div>
      </Card>
      <Card icon={Droplet} title="Usage tips">
        <ul className="space-y-1.5 text-sm text-white/70 font-sans">
          <li className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Mist linens lightly 30cm away — never soak</li>
          <li className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Spray air, not directly on wood / leather</li>
          <li className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Refresh curtains, throw pillows, towels weekly</li>
          <li className="flex gap-2"><span className="text-[#D4AF37]">◆</span> Set on bedside drawer for sleep ambiance</li>
        </ul>
      </Card>
    </div>
  );
}

/* ─── Body Mist ─── */
export function BodyMistBlocks() {
  return (
    <div className="space-y-4 my-6" data-testid="layout-body-mist">
      <Card icon={Sparkles} title="When to wear">
        <p className="text-sm text-white/70 font-sans">
          Light, refreshing, and easy to layer. Ideal for school, gym, daily errands, or as a backup spritz mid-day.
          Lower concentration than EDP/EDT, so you can spray generously without overpowering.
        </p>
      </Card>
      <Card icon={ShieldCheck} title="Safe for daily use">
        <p className="text-sm text-white/70 font-sans">
          Skin-friendly formula, alcohol-light. Perfect first scent for teens or anyone with mild skin sensitivity.
          Patch-test on inner wrist if you're trying a new scent.
        </p>
      </Card>
    </div>
  );
}

/* ─── Generic fallback for any other category ─── */
export function GenericBlocks() { return null; }

/* ─── Reusable card ─── */
function Card({ icon: Icon, title, children }) {
  return (
    <div className="bg-[#0F0F0F] border border-white/10 p-5 sm:p-6">
      <div className="flex items-center gap-2.5 mb-3">
        {Icon && <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />}
        <h3 className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] font-sans">{title}</h3>
      </div>
      {children}
    </div>
  );
}

/* ─── Dispatcher: pick the right block-set for a layout key ─── */
export default function CategorySections({ layout, product }) {
  if (!layout || layout === 'perfume' || layout === 'generic') return null;
  if (layout === 'helmet_spray') return <HelmetSprayBlocks product={product} />;
  if (layout === 'car_spray') return <CarSprayBlocks product={product} />;
  if (layout === 'home_linen') return <HomeLinenBlocks product={product} />;
  if (layout === 'body_mist') return <BodyMistBlocks product={product} />;
  return null;
}
